<template>
    <div
        v-show="visible && !publicMode"
        v-on-clickaway="close"
        class="context-menu"
        :style="style"
        tabindex="-1"
        @click="onClick"
        @contextmenu.capture.prevent
    >
        <ul class="context-menu__list">
            <slot :data="data" />
        </ul>
    </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import listenOnRootMixin from '@/mixins/listen-on-root';

export default {
    name: 'context-menu',
    mixins: [clickaway, listenOnRootMixin],

    props: {
        id: {
            type: String,
            required: true,
        },

        closeOnClick: {
            type: Boolean,
            default: true,
        },

        closeOnScroll: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            data: {},
            top: null,
            left: null,
            publicMode: process.env.VUE_APP_ENV === 'public',
            visible: false,
        };
    },

    computed: {
        style() {
            return this.visible
                ? { top: `${ this.top }px`, left: `${ this.left }px` }
                : null;
        },
    },

    watch: {
        closeOnScroll(value, oldValue) {
            if (value === oldValue) {
                return;
            }

            if (value) {
                this.addScrollEventListener();
            } else {
                this.removeScrollEventListener();
            }
        },
    },

    mounted() {
        if (this.closeOnScroll) {
            this.addScrollEventListener();
        }

        this.listenOnRoot('flex::context::open', this.open);
        this.listenOnRoot('flex::context::close', this.close);
    },

    beforeDestroy() {
        if (this.closeOnScroll) {
            this.removeScrollEventListener();
        }
    },

    methods: {
        addScrollEventListener() {
            window.addEventListener('scroll', this.close);
        },

        /**
         * Hides the context menu.
         *
         * @param {boolean|Event} emit prevent 2x emit when menu is clicked and closed
         */
        close(emit = true) {
            if (!this.visible) {
                return;
            }

            this.top = null;
            this.left = null;
            this.data = {};
            this.visible = false;

            if (emit) {
                this.$emit('close');
            }
        },

        onClick() {
            if (this.closeOnClick) {
                this.close(false);
            }
        },

        /**
         * Open the context menu.
         *
         * @param {MouseEvent} event
         * @param {string} id The ID of the context menu that is being opened
         * @param {array|object|string} data User provided data for the menu
         */
        async open({ event, id, data }) {
            if (id !== this.id) {
                this.close();
                return;
            }

            this.data = data;
            this.visible = true;

            await this.$nextTick();

            this.positionMenu(event.clientY, event.clientX);
            this.$emit('open');
        },

        positionMenu(top, left) {
            const largestHeight = window.innerHeight - this.$el.offsetHeight - 25;
            const largestWidth = window.innerWidth - this.$el.offsetWidth - 25;

            if (top > largestHeight) {
                top = largestHeight;
            }

            if (left > largestWidth) {
                left = largestWidth;
            }

            this.top = top;
            this.left = left;
        },

        removeScrollEventListener() {
            window.removeEventListener('scroll', this.close);
        },
    },
};
</script>

<style lang="scss">
.context-menu {
    position: fixed;
    z-index: 99999;
    color: $context-menu-color;
    background: $context-menu-background-color;
    border: 1px solid $context-menu-border-color;
    box-shadow: 0 0 10px -3px rgba(255, 255, 255, 0.5);

    &__list {
        list-style: none;
        margin: 0;
    }

    &__item {
        margin: 0;

        &:not(:first-of-type) {
            border-top: 1px solid $context-menu-divider-color;
        }
    }

    &__button {
        padding: 10px 40px 10px 15px;
        font-size: 12px;
        width: 100%;
        text-align: left;

        &:hover {
            background-color: $context-menu-hover-background-color;
        }
    }

    &__icon {
        margin-right: 10px;
    }
}
</style>
